// Admission-domain XLSX helpers for the bulk application intake flow.
//
// Three responsibilities (mirrors the expense bulk upload shape):
//   buildBulkApplicationsXlsx — generate a fillable template whose columns
//     come from THIS cycle's `formTemplate.sections[].fields[]`. Dropdowns
//     for system fields (gender, classId) and operator-defined dropdowns are
//     pre-populated via Excel data validation.
//   parseBulkApplicationsXlsx — read an uploaded XLSX, map columns back to
//     field keys via flexible header matching.
//   validateBulkApplicationRow — single source of truth for row validation.
//     Reuses `validateDynamicFields` from the admission form module so the
//     bulk sheet enforces identical rules to the public form + walk-in dialog.
//
// Nothing here hits the network — callers supply the cycle object (which
// already carries the template snapshot + allowed classes).

import {
  readFirstSheetAsRows,
  writeSheet,
  writeSheetWithReferences,
  type ReferenceList,
  type XlsxColumnSpec,
} from './xlsx';
import type { AdmissionCycle, AdmissionField, AdmissionFormSection, IdNamePair } from '../types/admissions';
import {
  isCnicFieldKey,
  splitFormValues,
  validateDynamicFields,
  withoutPhoto,
} from '../components/admissions/form/admissionFormFields';
import { cnicDigits } from './cnic';
import { classesTakingApplications } from './admissionClasses';

// Excel caps an inline list-validation formula at 255 characters including
// the wrapping double quotes. 5-char safety buffer.
function inlineListFits(values: string[]): boolean {
  const joined = values.join(',');
  return joined.length <= 250;
}

export type BulkApplicationFields = Record<string, string>;

/** Name on the template's sample row. A row still carrying it is skipped on
 *  upload, so a forgotten sample never becomes a real application. */
const EXAMPLE_ROW_NAME = 'Example — delete this row';

export type ParsedBulkApplicationRow = {
  rowNumber: number;
  fields: BulkApplicationFields;
  clientStatus: 'ok' | 'error';
  /** One-line summary of everything wrong with the row. */
  clientError?: string;
  /** Every problem, keyed by field — the review table marks each cell. */
  fieldErrors?: Record<string, string>;
  /** The cycle's eligibility rules would record this applicant as
   *  Ineligible (from the server preview). A warning, not an error: the
   *  school may still want the application on file. */
  ineligibleReasons?: string[];
};

/** Marks one field of a row as invalid (used by the CNIC duplicate checks
 *  that run after parsing), keeping the summary in step. */
export function withFieldError(
  row: ParsedBulkApplicationRow,
  fieldKey: string,
  message: string,
): ParsedBulkApplicationRow {
  const fieldErrors = { ...(row.fieldErrors || {}), [fieldKey]: message };
  return { ...row, clientStatus: 'error', fieldErrors, clientError: summarizeErrors(fieldErrors, null) };
}

export type BulkApplicationsLookups = {
  /** Lower-cased class name → classId. Resolves the display dropdown back
   *  to the ObjectId the backend wants. */
  classIdByName: Map<string, string>;
};

export function buildApplicationLookups(cycle: AdmissionCycle): BulkApplicationsLookups {
  const classIdByName = new Map<string, string>();
  for (const c of classesTakingApplications(cycle)) {
    if (c.name) classIdByName.set(c.name.trim().toLowerCase(), c._id);
  }
  return { classIdByName };
}

/** The fields we render as columns, in flow order. System fields always
 *  come first (operator-friendly), custom fields trail. */
function flattenEnabledFields(sections: AdmissionFormSection[]): AdmissionField[] {
  const out: AdmissionField[] = [];
  for (const s of sections) {
    for (const f of s.fields || []) {
      if (f.enabled === false) continue;
      out.push(f);
    }
  }
  return out;
}

function columnHeaderFor(field: AdmissionField): string {
  // Human-friendly label plus required marker. The parser does flexible
  // header matching so hyphens/spacing/case don't matter.
  const star = field.required ? ' *' : '';
  if (field.type === 'date') return `${field.label}${star} (yyyy-mm-dd)`;
  return `${field.label}${star}`;
}

function allowedValuesForField(
  field: AdmissionField,
  classOptions: IdNamePair[],
): string[] | null {
  if (field.key === 'classId') {
    return classOptions
      .map(c => c.name?.trim())
      .filter((v): v is string => !!v);
  }
  if (field.type === 'dropdown' || field.type === 'multiselect') {
    return (field.options || []).map(o => o.label);
  }
  if (field.type === 'boolean') return ['yes', 'no'];
  return null;
}

/** The cycle's form as the sheet carries it — everything but the photo,
 *  which a spreadsheet can't hold (it is added later from the drawer). */
export const bulkFormSections = (cycle: AdmissionCycle) =>
  withoutPhoto(cycle.formTemplate?.sections || []);

/**
 * Build the XLSX template for this cycle. Called when the operator clicks
 * "Download template" in the Bulk upload dialog.
 */
export async function buildBulkApplicationsXlsx(cycle: AdmissionCycle): Promise<Blob> {
  const sections = bulkFormSections(cycle);
  const fields = flattenEnabledFields(sections);
  const classOptions = classesTakingApplications(cycle);

  // Build column specs + collect any dropdown lists that need the hidden
  // reference sheet (lists longer than 250 chars).
  const columns: XlsxColumnSpec[] = [];
  const references: ReferenceList[] = [];

  for (const field of fields) {
    const header = columnHeaderFor(field);
    const spec: XlsxColumnSpec = {
      header,
      width: Math.max(field.label.length + 4, 14),
    };

    const values = allowedValuesForField(field, classOptions);
    if (values && values.length > 0) {
      if (inlineListFits(values)) {
        spec.validation = { kind: 'list', values };
      } else {
        const refName = `List_${field.key}`;
        references.push({ name: refName, values });
        spec.validation = { kind: 'list', values: [`@ref:${refName}`] };
      }
    } else if (field.type === 'number') {
      const min = field.validation?.min ?? 0;
      spec.validation = { kind: 'decimal-min', min };
    }

    columns.push(spec);
  }

  // Example row. For required fields we drop a placeholder so operators
  // see the shape; optional fields stay blank.
  const exampleRow = fields.map(field => {
    if (!field.required) return '';
    if (field.key === 'gender') return field.options?.[0]?.label ?? 'Male';
    if (field.key === 'cnic')          return '4210112345671';
    if (field.key === 'fatherCnic')    return '4210112345672';
    if (field.key === 'classId')       return classOptions[0]?.name ?? '';
    if (field.type === 'date')         return new Date().toISOString().slice(0, 10);
    if (field.type === 'number')       return 0;
    if (field.key === 'name')          return EXAMPLE_ROW_NAME;
    if (field.key === 'fatherName')    return 'Father name';
    if (field.key === 'fatherPhone' || field.key === 'candidateContact') return '03001234567';
    if (field.key === 'address')       return 'House #, Street, City';
    return '';
  });

  const config = {
    sheetName: 'Applications',
    columns,
    rows: [exampleRow],
    emptyRowsForValidation: 500,
  };

  return references.length === 0
    ? writeSheet(config)
    : writeSheetWithReferences({ ...config, references });
}

function summarizeErrors(fieldErrors: Record<string, string>, fields: AdmissionField[] | null): string {
  const labelOf = (k: string) => fields?.find(f => f.key === k)?.label ?? k;
  const parts = Object.entries(fieldErrors).map(([k, m]) => (fields ? `${labelOf(k)}: ${m}` : m));
  return parts.length === 1 ? parts[0] : `${parts.length} issues — ${parts.join(' · ')}`;
}

/**
 * Validate one row against the cycle's form. Uses the same
 * `validateDynamicFields` as the walk-in dialog and the public form, so
 * the sheet enforces identical rules — and reports EVERY problem in the
 * row, keyed by field, so the review table can mark each cell.
 */
export function validateBulkApplicationRow(
  fields: BulkApplicationFields,
  cycle: AdmissionCycle,
  lookups: BulkApplicationsLookups,
): Omit<ParsedBulkApplicationRow, 'rowNumber' | 'fields'> {
  const sections = bulkFormSections(cycle);
  const allFields = flattenEnabledFields(sections);

  // The sheet carries the class NAME; validation needs the id.
  const values: Record<string, any> = {};
  const fieldErrors: Record<string, string> = {};
  for (const [k, v] of Object.entries(fields)) {
    if (v == null || v === '') continue;
    if (k === 'classId') {
      const match = lookups.classIdByName.get(String(v).trim().toLowerCase());
      if (match) values[k] = match;
      else fieldErrors[k] = `"${v}" isn't a class taking applications in this cycle`;
    } else {
      const field = allFields.find(f => f.key === k);
      values[k] = field?.type === 'multiselect' ? String(v).split(',').map(x => x.trim()).filter(Boolean) : v;
    }
  }

  // Class lookup messages are more specific than the generic "Required".
  Object.assign(fieldErrors, { ...validateDynamicFields(values, sections), ...fieldErrors });
  if (Object.keys(fieldErrors).length === 0) return { clientStatus: 'ok', fieldErrors: undefined, clientError: undefined };
  return { clientStatus: 'error', fieldErrors, clientError: summarizeErrors(fieldErrors, allFields) };
}

/** Excel stores dates as serial day numbers; operators also type
 *  dd/mm/yyyy. Both become yyyy-mm-dd, the format the form uses.
 *  Anything else is left as typed so validation can flag it. */
function normalizeDateCell(raw: string): string {
  const v = raw.trim();
  if (/^\d{4,5}(\.\d+)?$/.test(v)) {
    const ms = Math.round((Number(v) - 25569) * 86400 * 1000);   // 25569 = 1970-01-01
    return new Date(ms).toISOString().slice(0, 10);
  }
  const dmy = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(v);
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(v);
  if (iso) return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`;
  return v;
}

/** The sheet shows option LABELS in its drop-downs; the form stores option
 *  VALUES. Accept either (any case) and store the value. Unknown text is
 *  kept so validation reports it. */
function normalizeChoiceCell(field: AdmissionField, raw: string): string {
  const match = (t: string) => {
    const low = t.trim().toLowerCase();
    return field.options?.find(o => o.value.toLowerCase() === low || o.label.toLowerCase() === low)?.value ?? t.trim();
  };
  return field.type === 'multiselect' ? raw.split(',').map(match).join(', ') : match(raw);
}

/**
 * Parse an uploaded XLSX file into row-shaped records. Headers are matched
 * flexibly against both `field.key` and `field.label` so operators who
 * tweak the header column names a little don't break the import.
 */
export async function parseBulkApplicationsXlsx(
  file: File,
  cycle: AdmissionCycle,
  lookups: BulkApplicationsLookups,
): Promise<{ rows: ParsedBulkApplicationRow[]; fileError?: string; skippedSampleRow?: boolean }> {
  const raw = await readFirstSheetAsRows(file);
  if (raw.length < 2) {
    return {
      rows: [],
      fileError: 'The file must have a header row and at least one data row.',
    };
  }

  const sections = bulkFormSections(cycle);
  const fields = flattenEnabledFields(sections);
  const headers = raw[0].map(h => (h || '').trim());

  // Build header → field.key map. Headers look like `Label *` or
  // `Label * (yyyy-mm-dd)`; labels themselves may contain brackets
  // ("CNIC / B-Form (13 digits)"). Compare with the required marker removed,
  // both with and without a trailing bracketed hint, case-insensitively.
  const clean = (h: string): string => h.replace(/\*/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
  const withoutHint = (h: string): string => h.replace(/\s*\([^)]*\)\s*$/, '').trim();

  const headerKeyByColIdx = new Map<number, string>();
  headers.forEach((h, colIdx) => {
    const candidates = new Set([clean(h), withoutHint(clean(h))]);
    const field = fields.find(f =>
      candidates.has(f.key.toLowerCase()) || candidates.has(clean(f.label)) || candidates.has(withoutHint(clean(f.label))));
    if (field && !Array.from(headerKeyByColIdx.values()).includes(field.key)) headerKeyByColIdx.set(colIdx, field.key);
  });

  if (headerKeyByColIdx.size === 0) {
    return {
      rows: [],
      fileError: `No columns in the file matched any template fields. Found headers: ${headers.filter(Boolean).join(', ')}`,
    };
  }

  // A missing required column would fail every row with the same message —
  // say it once, at file level, and name the columns.
  const mapped = new Set(headerKeyByColIdx.values());
  const missing = fields.filter(f => f.required && !mapped.has(f.key)).map(f => f.label);
  if (missing.length > 0) {
    return {
      rows: [],
      fileError: `The file is missing required column${missing.length === 1 ? '' : 's'}: ${missing.join(', ')}. Download a fresh template for this cycle.`,
    };
  }

  const rows: ParsedBulkApplicationRow[] = [];
  let skippedSampleRow = false;
  for (let i = 1; i < raw.length; i++) {
    const cols = raw[i];
    const parsedFields: BulkApplicationFields = {};
    headerKeyByColIdx.forEach((fieldKey, colIdx) => {
      const value = (cols[colIdx] ?? '').trim();
      if (value) parsedFields[fieldKey] = value;
    });

    // Skip fully blank rows so trailing empty rows don't produce
    // "name is required" × 100, and the template's untouched sample row.
    if (Object.keys(parsedFields).length === 0) continue;
    if (parsedFields.name === EXAMPLE_ROW_NAME) { skippedSampleRow = true; continue; }

    // CNICs: digits-only normalisation (the template says "13 digits"
    // but operators paste anything).
    for (const cnicKey of Object.keys(parsedFields).filter(isCnicFieldKey)) {
      if (parsedFields[cnicKey]) {
        parsedFields[cnicKey] = cnicDigits(parsedFields[cnicKey]);
      }
    }

    for (const field of fields) {
      const raw = parsedFields[field.key];
      if (!raw) continue;
      if (field.type === 'boolean') {
        // "yes" / "no" / true / false → canonical "true" / "false".
        const low = raw.toLowerCase();
        parsedFields[field.key] = low === 'yes' || low === 'true' || low === '1' ? 'true' : 'false';
      } else if (field.type === 'date') {
        parsedFields[field.key] = normalizeDateCell(raw);
      } else if ((field.type === 'dropdown' || field.type === 'multiselect') && field.key !== 'classId') {
        parsedFields[field.key] = normalizeChoiceCell(field, raw);
      }
    }

    const validated = validateBulkApplicationRow(parsedFields, cycle, lookups);
    rows.push({
      rowNumber: rows.length + 1,
      fields: parsedFields,
      ...validated,
    });
  }

  return { rows, skippedSampleRow };
}

/**
 * Shape ok rows into the wire payload. Each row becomes a `topLevel` dict
 * (system fields hoisted to top-level AdmissionApplication columns) + a
 * `formData` dict (operator-defined custom fields). classId is already
 * resolved to the ObjectId by the validator.
 */
export function toBulkApplicationsPayload(
  rows: ParsedBulkApplicationRow[],
  cycle: AdmissionCycle,
  lookups: BulkApplicationsLookups,
): Array<{
  rowNumber: number;
  payload: Record<string, any>;
}> {
  const sections = bulkFormSections(cycle);

  return rows
    .filter(r => r.clientStatus === 'ok')
    .map(r => {
      // Resolve classId first — the fields object still carries class NAME.
      const resolvedFields: Record<string, any> = { ...r.fields };
      if (resolvedFields.classId) {
        const match = lookups.classIdByName.get(String(resolvedFields.classId).trim().toLowerCase());
        if (match) resolvedFields.classId = match;
      }
      for (const f of flattenEnabledFields(sections)) {
        if (f.type === 'multiselect' && typeof resolvedFields[f.key] === 'string') {
          resolvedFields[f.key] = resolvedFields[f.key].split(',').map((x: string) => x.trim()).filter(Boolean);
        }
      }
      const { topLevel, formData } = splitFormValues(resolvedFields, { sections });
      return {
        rowNumber: r.rowNumber,
        payload: {
          ...topLevel,
          formData: Object.keys(formData).length > 0 ? formData : undefined,
        },
      };
    });
}

